import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import InforCircleIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/information-circle';
import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n/index';
import { withMiniEventTracker } from '../../contexts/mini-events/index';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './AtypicalDealsDescription.module.scss';

import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';

const cls = cssModules(STYLES);

type Props = {
  description: string | ReactNode;
  explanation: string;
  i18n: I18nService;
  isFromDV: boolean;
  miniEventTracker: MiniEventTracker;
};

const AtypicalDealsDescription = ({
  description,
  explanation,
  i18n,
  isFromDV,
  miniEventTracker,
}: Props) => {
  const targetRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cls('AtypicalDealsDescription')}>
      <BpkText className={cls('AtypicalDealsDescription__detail')}>
        {description}
      </BpkText>
      <BpkPopover
        label={i18n.translate('hd_price_range_tip')}
        ariaLabel={i18n.translate('hd_price_range_tip')}
        id="atypical-deals-tooltip"
        target={
          <button
            type="button"
            ref={targetRef}
            aria-label={i18n.translate('hd_price_range_tip')}
            className={cls('AtypicalDealsDescription__icon')}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen((prevIsOpen) => !prevIsOpen);
              if (!isOpen) {
                miniEventTracker.trackHotelsAction(
                  ACTION_TYPE.VIEW_BUTTON_CLICKED,
                  buildAdditionalInfoViewButtonClicked(
                    isFromDV
                      ? CLICK_AREA.PRICE_RANGES_INFO_ICON_BUTTON
                      : CLICK_AREA.PRICE_RANGES_DETAILS_INFO_ICON_BUTTON,
                  ),
                );
              }
            }}
          >
            <InforCircleIcon data-testid="atypical-deals-tooltip-icon" />
          </button>
        }
        isOpen={isOpen}
        onClose={(e) => {
          e?.preventDefault();
          e?.stopPropagation();
          setIsOpen(false);
        }}
        actionText={i18n.translate('Modal_close')}
        onAction={() => {
          setIsOpen(false);
        }}
      >
        <BpkText
          textStyle={TEXT_STYLES.footnote}
          tagName="p"
          className={cls('AtypicalDealsDescription__popover')}
        >
          {explanation}
        </BpkText>
      </BpkPopover>
    </div>
  );
};

export default withMiniEventTracker(withI18n(AtypicalDealsDescription));
